import type { CatalogSection } from '@/features/openTabs/types'
import Fuse from 'fuse.js'
import { useMemo, useState } from 'react'

const useCatalogSearch = (sections: readonly CatalogSection[], selectedCategoryId: string) => {
    const [searchOpen, setSearchOpen] = useState(false)
    const [searchValue, setSearchValue] = useState('')
    const searchTerm = searchValue.trim()

    const searchIndex = useMemo(
        () =>
            new Fuse(
                sections.flatMap((section) => section.products),
                { keys: ['name'], ignoreDiacritics: true }
            ),
        [sections]
    )

    const visibleSections = useMemo(() => {
        if (!searchTerm) {
            return sections.filter((section) => section.categoryId === selectedCategoryId)
        }

        const matchingIds = new Set(searchIndex.search(searchTerm).map(({ item }) => item.id))
        const includedIds = new Set<string>()

        return sections.flatMap((section) => {
            const products = section.products.filter((product) => {
                if (!matchingIds.has(product.id) || includedIds.has(product.id)) return false

                includedIds.add(product.id)
                return true
            })

            return products.length > 0 ? [{ ...section, products }] : []
        })
    }, [searchIndex, searchTerm, sections, selectedCategoryId])

    const closeSearch = () => {
        setSearchOpen(false)
        setSearchValue('')
    }

    return {
        searchOpen,
        searchValue,
        searching: searchTerm.length > 0,
        visibleSections,
        openSearch: () => setSearchOpen(true),
        closeSearch,
        setSearchValue,
    }
}

export default useCatalogSearch
